import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';

const Main: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bannerImagePath = '/images/main/banner.webp';
  
  const factoryImages = [
    '/images/factory/factory1.webp',
    '/images/factory/factory2.webp',
    '/images/factory/factory3.webp',
    '/images/factory/factory4.webp',
    '/images/factory/factory5.webp',
    '/images/factory/factory6.webp',
    '/images/factory/factory7.webp',
    '/images/factory/factory8.webp',
    '/images/about/cert1.webp',
    '/images/about/cert2.webp',
    '/images/about/cert3.webp',
    '/images/about/cert4.webp',
    '/images/about/cert5.webp',
    '/images/about/cert6.webp',
    '/images/about/cert7.webp',
    '/images/about/cert8.webp'
  ];

  // 跑马灯配置参数
  const MARQUEE_CONFIG = {
    imageWidth: 280,
    imageMargin: 16,
    scrollStep: 296,
    animationDuration: 10,
    pauseDuration: 2000,
    transitionDuration: 800
  };

  // 跑马灯控制状态
  const [translateX, setTranslateX] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationRef = useRef<number | null>(null);

  // 视频播放控制状态
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasVideoStarted, setHasVideoStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 计算单组图片的总宽度
  const singleSetWidth = factoryImages.length * MARQUEE_CONFIG.scrollStep;
  
  // 初始化位置：从第二组开始显示，这样可以向左右两个方向滚动
  const initialOffset = -singleSetWidth;

  // 手动向左滚动（内容向左移动，显示下一张图片）
  const scrollLeft = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setIsPaused(true);
    
    setTranslateX(prev => prev - MARQUEE_CONFIG.scrollStep);
    
    // 清除之前的过渡定时器
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    // 等待过渡完成后恢复自动滚动
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
      setIsPaused(false);
    }, MARQUEE_CONFIG.transitionDuration);
  };

  // 手动向右滚动（内容向右移动，显示上一张图片）
  const scrollRight = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setIsPaused(true);
    
    setTranslateX(prev => prev + MARQUEE_CONFIG.scrollStep);
    
    // 清除之前的过渡定时器
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    // 等待过渡完成后恢复自动滚动
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
      setIsPaused(false);
    }, MARQUEE_CONFIG.transitionDuration);
  };

  // 视频播放控制函数
  const handleVideoPlay = async () => {
    if (!videoRef.current) return;

    try {
      setIsVideoLoading(true);
      
      if (!hasVideoStarted) {
        // 首次播放时加载视频
        videoRef.current.load();
        setHasVideoStarted(true);
      }

      if (isVideoPlaying) {
        // 如果正在播放，则暂停
        videoRef.current.pause();
      } else {
        // 如果暂停中，则播放
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('视频播放失败:', error);
    } finally {
      setIsVideoLoading(false);
    }
  };

  // 处理进度条拖动
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const newTime = parseFloat(e.target.value);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // 处理音量控制
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  // 切换静音
  const handleToggleMute = () => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.volume = volume > 0 ? volume : 0.5;
      setVolume(volume > 0 ? volume : 0.5);
      setIsMuted(false);
    } else {
      videoRef.current.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  // 全屏控制函数
  const handleFullscreen = async () => {
    if (!videoContainerRef.current) return;

    const element = videoContainerRef.current as any;
    const doc = document as any;

    try {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );

      if (!isCurrentlyFullscreen) {
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('全屏操作失败:', error);
    }
  };

  // 格式化时间
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 显示/隐藏控制栏
  const showControlsBar = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isVideoPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // 监听视频播放状态
  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
    setShowControls(true);
  };

  const handleVideoLoadStart = () => {
    setIsVideoLoading(true);
  };

  const handleVideoCanPlay = () => {
    setIsVideoLoading(false);
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoPlayEvent = () => {
    setIsVideoPlaying(true);
    showControlsBar();
  };

  const handleVideoPauseEvent = () => {
    setIsVideoPlaying(false);
    setShowControls(true);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setVolume(videoRef.current.volume);
      setIsMuted(videoRef.current.muted);
    }
  };

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as any;
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // 清理控制栏隐藏定时器
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // 自动滚动逻辑
  useEffect(() => {
    if (isPaused || isTransitioning) return;
    
    const animate = () => {
      setTranslateX(prev => prev + 1); // 每帧向右移动1px
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, isTransitioning]);
  
  // 管理无缝滚动位置重置
  useEffect(() => {
    // 当translateX移动到一定位置时，无缝重置位置
    if (translateX >= initialOffset + singleSetWidth) {
      // 向右滚动太远，重置到中间位置
      setTranslateX(initialOffset);
    } else if (translateX <= initialOffset - singleSetWidth) {
      // 向左滚动太远，重置到中间位置
      setTranslateX(initialOffset);
    }
  }, [translateX, initialOffset, singleSetWidth]);
  
  // 清理定时器和动画
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  // 初始化translateX位置
  useEffect(() => {
    setTranslateX(initialOffset);
  }, [initialOffset]);
  
  // 生成要显示的图片序列（3组：前一组、当前组、后一组）
  const getDisplayImages = () => {
    const images = [];
    
    // 生成3组图片用于无缝滚动
    for (let group = 0; group < 3; group++) {
      for (let i = 0; i < factoryImages.length; i++) {
        const imageIndex = i;
        images.push({
          src: factoryImages[imageIndex],
          key: `group-${group}-${i}`,
          alt: `跑马灯展示图 ${imageIndex + 1}`
        });
      }
    }
    
    return images;
  };
  
  return (
    <main className="main-container">
      <section className="banner-section">
        <div 
          className="banner-background"
          style={{
            backgroundImage: `url('${bannerImagePath}')`
          }}
        >
          <div className="banner-overlay">
            <div className="banner-content">
              <h1 className="banner-title">
                {t('main.banner.title')}
              </h1>
              <p className="banner-subtitle">
                {t('main.banner.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* 卖点介绍区域 */}
      <section className="highlights-section">
        <div className="highlights-container">
          <div className="highlights-grid">
            <div className="highlight-item">
              <div className="highlight-number">{t('main.highlights.experience.number')}</div>
              <div className="highlight-label">{t('main.highlights.experience.label')}</div>
            </div>
            <div className="highlight-item">
              <div className="highlight-number">{t('main.highlights.capacity.number')}</div>
              <div className="highlight-label">{t('main.highlights.capacity.label')}</div>
            </div>
            <div className="highlight-item">
              <div className="highlight-number">{t('main.highlights.employees.number')}</div>
              <div className="highlight-label">{t('main.highlights.employees.label')}</div>
            </div>
            <div className="highlight-item">
              <div className="highlight-number">{t('main.highlights.regions.number')}</div>
              <div className="highlight-label">{t('main.highlights.regions.label')}</div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="brand-intro-section">
        <div className="brand-intro-container">
          <div className="brand-intro-content">
            <div className="brand-intro-text-area">
              <p className="brand-intro-text">
                {t('main.brandIntro.text1')}
              </p>
              <p className="brand-intro-text">
                {t('main.brandIntro.text2')}
              </p>
              <button 
                className="learn-more-link"
                onClick={() => {
                  navigate('/about');
                  window.scrollTo(0, 0);
                }}
              >
                {t('common.learnMore')}
              </button>
            </div>
            <div className="brand-intro-image-area">
              <div 
                className="video-container"
                ref={videoContainerRef}
                onMouseMove={showControlsBar}
                onMouseLeave={() => {
                  if (isVideoPlaying) {
                    setShowControls(false);
                  }
                }}
              >
                <video 
                  ref={videoRef}
                  className="brand-intro-video"
                  poster="/images/main/intro.webp"
                  onEnded={handleVideoEnded}
                  onLoadStart={handleVideoLoadStart}
                  onCanPlay={handleVideoCanPlay}
                  onPlay={handleVideoPlayEvent}
                  onPause={handleVideoPauseEvent}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  preload="none"
                >
                  {hasVideoStarted && <source src="/videos/intro.mp4" type="video/mp4" />}
                  {t('main.brandIntro.videoNotSupported')}
                </video>
                
                {/* 中央播放按钮覆盖层 */}
                {!hasVideoStarted && (
                  <div className="video-play-overlay" onClick={handleVideoPlay}>
                    {isVideoLoading ? (
                      <div className="video-loading">
                        <div className="loading-spinner"></div>
                      </div>
                    ) : (
                      <div className="video-play-button">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                  </div>
                )}

                {/* 视频控制栏 */}
                <div className={`video-controls ${showControls ? 'show' : ''}`}>
                  {/* 进度条 */}
                  <div className="video-progress-container">
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleProgressChange}
                      className="video-progress-bar"
                      style={{
                        '--progress-percent': duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                      } as React.CSSProperties & { '--progress-percent': string }}
                    />
                  </div>

                  {/* 控制按钮区域 */}
                  <div className="video-controls-bar">
                    <div className="video-controls-left">
                      {/* 播放/暂停按钮 */}
                      <button 
                        className="video-control-button"
                        onClick={handleVideoPlay}
                        aria-label={isVideoPlaying ? t('main.brandIntro.pause') : t('main.brandIntro.play')}
                      >
                        {isVideoPlaying ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                            <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M8 5v14l11-7z" fill="currentColor"/>
                          </svg>
                        )}
                      </button>

                      {/* 时间显示 */}
                      <span className="video-time">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>

                    <div className="video-controls-right">
                      {/* 音量控制 */}
                      <div className="video-volume-control">
                        <button 
                          className="video-control-button"
                          onClick={handleToggleMute}
                          aria-label={isMuted ? t('main.brandIntro.unmute') : t('main.brandIntro.mute')}
                        >
                          {isMuted || volume === 0 ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38.31 2.63.95 3.69 1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
                            </svg>
                          ) : volume < 0.5 ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" fill="currentColor"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"/>
                            </svg>
                          )}
                        </button>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="video-volume-bar"
                          style={{
                            '--volume-percent': `${(isMuted ? 0 : volume) * 100}%`
                          } as React.CSSProperties & { '--volume-percent': string }}
                        />
                      </div>

                      {/* 全屏按钮 */}
                      <button 
                        className="video-control-button"
                        onClick={handleFullscreen}
                        aria-label={isFullscreen ? t('main.brandIntro.exitFullscreen') : t('main.brandIntro.fullscreen')}
                      >
                        {isFullscreen ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3v5m0 0H3m5 0v5M16 3v5m0 0h5m-5 0v5M8 21v-5m0 0H3m5 0v-5m8 5v-5m0 0h5m-5 0v-5"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 工厂展示跑马灯组件 */}
      <section className="factory-marquee-section">
        <div className="factory-marquee-container">
          <div className="factory-marquee-controls">
            <button 
              className={`marquee-arrow marquee-arrow-left ${isTransitioning ? 'disabled' : ''}`}
              onClick={scrollRight}
              disabled={isTransitioning}
              aria-label={t('main.brandIntro.scrollLeft')}
            >
              &#8249;
            </button>
            
            <div className="factory-marquee-wrapper">
              <div 
                ref={marqueeRef}
                className={`factory-marquee-track ${isPaused ? 'paused' : ''} ${isTransitioning ? 'transitioning' : ''}`}
                style={{
                  transform: `translateX(${translateX}px)`,
                  '--marquee-duration': `${MARQUEE_CONFIG.animationDuration}s`,
                  transition: isTransitioning ? `transform ${MARQUEE_CONFIG.transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)` : 'none'
                } as React.CSSProperties & { 
                  '--marquee-duration': string;
                }}
              >
                {/* 使用新的图片生成逻辑 */}
                {getDisplayImages().map((image) => (
                  <div key={image.key} className="factory-marquee-item">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="factory-marquee-image"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <button 
              className={`marquee-arrow marquee-arrow-right ${isTransitioning ? 'disabled' : ''}`}
              onClick={scrollLeft}
              disabled={isTransitioning}
              aria-label={t('main.brandIntro.scrollRight')}
            >
              &#8250;
            </button>
          </div>
        </div>
      </section>

      {/* 业务和服务项目区域 */}
      <section className="business-services-section">
        <div className="business-services-container">
          <h2 className="business-services-title">{t('main.businessServices.title')}</h2>
          <div className="business-services-grid">
            <div 
              className="business-service-item"
              onClick={() => {
                navigate('/services');
                window.scrollTo(0, 0);
              }}
            >
              <div className="service-image-wrapper">
                <img 
                  src="/images/main/product.webp" 
                  alt={t('main.businessServices.productIntro.title')}
                  className="service-image"
                />
                <div className="service-overlay">
                  <span className="service-overlay-text">{t('common.learnMore')}</span>
                </div>
              </div>
              <h3 className="service-title">{t('main.businessServices.productIntro.title')}</h3>
              <p className="service-description">
                {t('main.businessServices.productIntro.description')}
              </p>
            </div>
            
            <div 
              className="business-service-item"
              onClick={() => {
                navigate('/services');
                window.scrollTo(0, 0);
              }}
            >
              <div className="service-image-wrapper">
                <img 
                  src="/images/main/cooperation.webp" 
                  alt={t('main.businessServices.cooperation.title')}
                  className="service-image"
                />
                <div className="service-overlay">
                  <span className="service-overlay-text">{t('common.learnMore')}</span>
                </div>
              </div>
              <h3 className="service-title">{t('main.businessServices.cooperation.title')}</h3>
              <p className="service-description">
                {t('main.businessServices.cooperation.description')}
              </p>
            </div>
            
            <div 
              className="business-service-item"
              onClick={() => {
                navigate('/about');
                // 延迟滚动到工厂实力部分，确保页面加载完成
                setTimeout(() => {
                  const factorySection = document.getElementById('factory-strength');
                  if (factorySection) {
                    factorySection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
            >
              <div className="service-image-wrapper">
                <img 
                  src="/images/main/factory.webp" 
                  alt={t('main.businessServices.factory.title')}
                  className="service-image"
                />
                <div className="service-overlay">
                  <span className="service-overlay-text">{t('common.learnMore')}</span>
                </div>
              </div>
              <h3 className="service-title">{t('main.businessServices.factory.title')}</h3>
              <p className="service-description">
                {t('main.businessServices.factory.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default Main;
